import { useGcal } from '@geerd-nx/gcal';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { UseTusOptions, useTus } from 'use-tus';

export default function useTusVideoUpload({
  ASSETS_API,
  onSuccess,
  onError,
  useTusOptions,
}: {
  ASSETS_API: string;
  onError?: ((error: Error) => void) | null | undefined;
  onSuccess?: (path: string) => void;
  useTusOptions?: UseTusOptions;
}) {
  const { setUpload, upload, isSuccess, isAborted, error, remove } =
    useTus(useTusOptions);
  const [uploadProgress, setProgress] = useState(0);
  const { getSession } = useGcal();

  const handleSetUpload = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      // get signed post request
      const session = await getSession();
      const token = session.getAccessToken().getJwtToken();
      const file = event.target.files?.item(0);
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = function () {
        window.URL.revokeObjectURL(video.src);
        if (!file) {
          return;
        }

        setUpload(file, {
          endpoint: `${ASSETS_API}/api/video/tus-upload-url?token=${token}`,
          metadata: {
            filename: file.name,
            name: file.name,
            filetype: file.type,
            filesize: file.size.toString(),
            maxDurationSeconds: Math.round(video.duration).toString(),
            metadata: JSON.stringify({ origin: window.location.origin }),
          },
          chunkSize: 10 * 1024 * 1024, // Required a minimum chunk size of 15MB, here we use 50MB.
          retryDelays: [0, 3000, 5000, 10000, 20000],

          onError,

          onProgress: function (bytesUploaded, bytesTotal) {
            const percentage = ((bytesUploaded / bytesTotal) * 100).toFixed(2);
            setProgress(+percentage);
            console.log(bytesUploaded, bytesTotal, percentage + '%');
          },
        });
      };
      video.src = URL.createObjectURL(file as File);
    },
    [setUpload]
  );

  const handleStart = useCallback(() => {
    if (!upload) {
      return;
    }
    // Check if there are any previous uploads to continue.
    upload.findPreviousUploads().then(function (previousUploads) {
      // Found previous uploads so we select the first one.
      if (previousUploads.length) {
        upload.resumeFromPreviousUpload(previousUploads[0]);
      }

      // Start the upload
      upload.start();
    });
  }, [upload]);

  const handleAbort = useCallback(() => {
    if (!upload) {
      return;
    }

    upload.abort();
  }, [upload]);

  useEffect(() => {
    if (isSuccess) {
      if (onSuccess) {
        onSuccess(`cloudflare://${upload?.url?.split('/')[4].split('?')[0]}`);
      }
    }
  }, [isSuccess, upload]);

  return {
    handleSetUpload,
    handleStart,
    handleAbort,
    result: {
      isSuccess,
      isAborted,
      error,
      uploadProgress,
      upload,
      remove,
      isUploading: uploadProgress > 0 && uploadProgress < 100,
    },
  };
}
